/**
 * useRowSelection — in-memory row selection for App-mode lists (Operator Lists v2.1).
 * Keys a Map by row id, storing the row SNAPSHOT at select time so selection-scoped
 * aggregates and bulk export survive windowed-cache eviction. Resets on scope change.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface RowSelectionApi {
  selectedIds: Set<string>
  selectedRows: Record<string, unknown>[]
  count: number
  isSelected: (id: string) => boolean
  toggle: (id: string, row: Record<string, unknown>) => void
  selectMany: (rows: Record<string, unknown>[]) => void
  deselectMany: (ids: string[]) => void
  clear: () => void
  /**
   * Refresh the stored snapshot for a selected row in lockstep with the windowed
   * cache (see `RuntimeListView`'s `patchRowAndSelection`). No-op — returns the
   * SAME map, preserving identity — when `id` isn't selected, so unselected
   * edits don't re-render selection-derived state.
   */
  patch: (id: string, updater: (row: Record<string, unknown>) => Record<string, unknown>) => void
}

export function useRowSelection(scopeKey: string): RowSelectionApi {
  const [selected, setSelected] = useState<Map<string, Record<string, unknown>>>(new Map())

  useEffect(() => {
    setSelected(new Map())
  }, [scopeKey])

  const toggle = useCallback((id: string, row: Record<string, unknown>) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(id)) next.delete(id)
      else next.set(id, row)
      return next
    })
  }, [])

  const selectMany = useCallback((rows: Record<string, unknown>[]) => {
    setSelected((prev) => {
      const next = new Map(prev)
      for (const r of rows) {
        const id = String(r.id)
        if (id) next.set(id, r)
      }
      return next
    })
  }, [])

  const deselectMany = useCallback((ids: string[]) => {
    setSelected((prev) => {
      const next = new Map(prev)
      for (const id of ids) next.delete(id)
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Map()), [])
  const isSelected = useCallback((id: string) => selected.has(id), [selected])

  const patch = useCallback((id: string, updater: (row: Record<string, unknown>) => Record<string, unknown>) => {
    setSelected((prev) => {
      const cur = prev.get(id)
      if (cur === undefined) return prev
      const next = new Map(prev)
      next.set(id, updater(cur))
      return next
    })
  }, [])

  const selectedIds = useMemo(() => new Set(selected.keys()), [selected])
  const selectedRows = useMemo(() => Array.from(selected.values()), [selected])

  return { selectedIds, selectedRows, count: selected.size, isSelected, toggle, selectMany, deselectMany, clear, patch }
}
